import React, { useRef, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Link } from 'react-router-dom';
import { 
  FaMapMarkerAlt, FaPaperPlane, FaGlobeAmericas, 
  FaChevronRight, FaCheckCircle, FaStar, FaChevronDown, 
  FaTimesCircle, FaClock, FaBus, FaHotel, FaUtensils, FaShieldAlt 
} from 'react-icons/fa';
import './JagannathPuriYatra.css';

const JagannathPuriYatra = () => {
  const [openSection, setOpenSection] = useState(null);

  const itineraryRef = useRef(null);
  const highlightsRef = useRef(null);
  const inclusionsRef = useRef(null);
  const aboutRef = useRef(null);

  const scrollToSection = (ref) => {
    window.scrollTo({ top: ref.current.offsetTop - 100, behavior: 'smooth' });
  };

  const fadeInUp = {
    hidden: { opacity: 0, y: 40 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.7, ease: "easeOut" } }
  };

  // JAGANNATH PURI ITINERARY DATA
  const itineraryData = [
    {
      day: 1,
      title: "Arrival in Bhubaneswar & Local Sightseeing",
      points: [
        "Arrival at Bhubaneswar Airport / Railway Station and transfer to hotel.",
        "Proceed for local sightseeing: Shree Lingaraj Temple, Ananta Basudev Temple.",
        "Visit Shree Kedargouri Temple and Raja Rani Temple.",
        "Overnight stay in Bhubaneswar."
      ],
      icon: <FaBus />
    },
    {
      day: 2,
      title: "Bhubaneswar to Puri (Via Konark)",
      points: [
        "Travel towards Puri via Dhauli Shanti Stupa and Chausath Yogini Temple.",
        "Stop at Pipili Chadua Market for appliqué handicrafts.",
        "Visit the world-famous Konark Sun Temple and Chandrabhaga Beach.",
        "Drive through the scenic Konark – Puri Marine Drive.",
        "Overnight stay in Puri."
      ],
      icon: <FaMapMarkerAlt />
    },
    {
      day: 3,
      title: "Puri Local Darshan",
      points: [
        "Shree Jagannath Temple Darshan and Mahaprasad at Anand Bazaar.",
        "Visit Narendra Kona, Bedi Hanuman, and Shree Lokanath Temple.",
        "Explore Mausi Maa Temple and Maa Bata Mangala Temple.",
        "Evening at Golden Beach, Blue Flag Beach, and local markets.",
        "Overnight stay in Puri."
      ],
      icon: <FaGlobeAmericas />
    },
    {
      day: 4,
      title: "Chilika Lake Excursion & Departure to Kolkata",
      points: [
        "Excursion to Chilika Lake (largest brackish water lake in Asia).",
        "Boat ride to spot Irrawaddy Dolphins and visit Kalijai Maa Temple.",
        "Visit Alarnath Temple and Raghurajpur Patachitra Village.",
        "Transfer to Puri Railway Station for overnight journey to Kolkata."
      ],
      icon: <FaPaperPlane />
    },
    {
      day: 5,
      title: "Arrival in Kolkata & City Tour",
      points: [
        "Arrival in Kolkata and hotel check-in.",
        "Visit Victoria Memorial, Howrah Bridge, and Indian Museum.",
        "Darshan at Kalighat Kali Temple and Dakshineswar Kali Temple.",
        "Visit Belur Math and Alipore Zoological Garden.",
        "Overnight stay in Kolkata."
      ],
      icon: <FaBus />
    },
    {
      day: 6,
      title: "Gangasagar Pilgrimage",
      points: [
        "Early morning drive to Gangasagar (100 km from Kolkata).",
        "Take a holy dip at the confluence of Ganga and Bay of Bengal.",
        "Darshan at Kapil Muni Temple.",
        "Return to Kolkata for overnight stay."
      ],
      icon: <FaShieldAlt />
    },
    {
      day: 7,
      title: "Departure",
      points: [
        "Breakfast and hotel check-out.",
        "Transfer to Kolkata Airport / Railway Station for onward journey.",
        "Tour concludes with divine blessings."
      ],
      icon: <FaCheckCircle />
    }
  ];

  return (
    <div className="trip-container">
      {/* 1. HEADER */}
      <motion.header initial="hidden" whileInView="visible" viewport={{ once: false }} variants={fadeInUp} className="trip-header">
        <div className="header-left">
          <h1 className="trip-title">Jagannath Puri Divine Darshan Yatra – <br /> Bhubaneswar, Puri, Konark, Chilika & Kolkata</h1>
          <p className="trip-duration">Duration: 6 nights & 7 days of Spiritual Bliss</p>
        </div>
        <div className="header-right">
          <div className="info-row"><FaMapMarkerAlt className="header-icon" /> <div className="info-text"><span>Destination:</span><p>Puri, Konark, Kolkata, Gangasagar</p></div></div>
          <div className="info-row"><FaPaperPlane className="header-icon" /> <div className="info-text"><span>Service Type:</span><p>Religious & Cultural Tour</p></div></div>
          <div className="info-row type-tag"><FaGlobeAmericas className="header-icon" /><p>Pilgrimage Special</p></div>
        </div>
      </motion.header>

      {/* 2. IMAGE GRID (Images can be changed by you) */}
      <motion.div initial="hidden" whileInView="visible" viewport={{ once: false }} variants={fadeInUp} className="image-grid-container">
        <div className="grid-item large-vertical">
          <img src="https://s3.ap-south-1.amazonaws.com/awsimages.imagesbazaar.com/1200x1800-old/11758/HQ356327.jpg?date=Thu%20Mar%2026%202026%2010:09:30%20GMT+0530%20(India%20Standard%20Time)" alt="Jagannath Temple" />
        </div>
        <div className="grid-item middle-box">
          <img src="https://s3.ap-south-1.amazonaws.com/awsimages.imagesbazaar.com/1200x1800-old/4421/HQ123664.jpg?date=Thu%20Mar%2026%202026%2010:08:31%20GMT+0530%20(India%20Standard%20Time)" alt="Konark Sun Temple" />
        </div>
        <div className="right-stack">
          <div className="grid-item small-h">
            <img src="https://s3.ap-south-1.amazonaws.com/awsimages.imagesbazaar.com/1200x1800-old/16163/DK680726.jpg?date=Thu%20Mar%2026%202026%2010:10:39%20GMT+0530%20(India%20Standard%20Time)" alt="Victoria Memorial" />
          </div>
          <div className="grid-item small-h">
            <img src="https://s3.ap-south-1.amazonaws.com/awsimages.imagesbazaar.com/1200x1800-old/14965/HQ579815.jpg?date=Thu%20Mar%2026%202026%2010:11:18%20GMT+0530%20(India%20Standard%20Time)" alt="Chilika Lake" />
          </div>
        </div>
      </motion.div>

      {/* 3. STICKY NAV */}
      <nav className="sticky-nav">
        <button className="tab-link" onClick={() => scrollToSection(itineraryRef)}>Itinerary</button>
        <button className="tab-link" onClick={() => scrollToSection(highlightsRef)}>Highlights</button>
        <button className="tab-link" onClick={() => scrollToSection(inclusionsRef)}>Policy</button>
        <button className="tab-link" onClick={() => scrollToSection(aboutRef)}>Overview</button>
      </nav>

      <div className="content-section-wrapper">
        {/* ITINERARY SECTION */}
        <section ref={itineraryRef} className="scroll-section">
          <h2 className="section-main-title">Day Wise Itinerary</h2>
          <div className="days-list">
            {itineraryData.map((item) => (
              <motion.div key={item.day} initial="hidden" whileInView="visible" viewport={{ once: false, amount: 0.2 }} variants={fadeInUp} className="day-block">
                <div className="day-header">
                  <span className="day-badge">Day {item.day}</span>
                  <h3>{item.title}</h3>
                </div>
                <div className="day-body">
                  {item.points.map((p, i) => (
                    <div key={i} className="point-item">
                      <FaChevronRight className="bullet-icon" />
                      <p>{p}</p>
                    </div>
                  ))}
                </div>
              </motion.div>
            ))}
          </div>
        </section>

        {/* HIGHLIGHTS SECTION */}
        <motion.section ref={highlightsRef} initial="hidden" whileInView="visible" viewport={{ once: false }} variants={fadeInUp} className="scroll-section">
          <h2 className="section-main-title">Trip Highlights</h2>
          <div className="highlights-grid">
            <div className="h-item"><FaStar className="star-icon" /> <p>Divine darshan at Shree Jagannath Temple, Puri</p></div>
            <div className="h-item"><FaStar className="star-icon" /> <p>Visit the world-famous Konark Sun Temple</p></div>
            <div className="h-item"><FaStar className="star-icon" /> <p>Boating at Chilika Lake and spotting Irrawaddy Dolphins</p></div>
            <div className="h-item"><FaStar className="star-icon" /> <p>Visit Kalighat & Dakshineswar Kali Temples in Kolkata</p></div>
            <div className="h-item"><FaStar className="star-icon" /> <p>Holy pilgrimage to Gangasagar and Kapil Muni Temple</p></div>
            <div className="h-item"><FaStar className="star-icon" /> <p>Explore historic landmarks like Victoria Memorial and Howrah Bridge</p></div>
          </div>
        </motion.section>

        {/* INCLUSIONS & EXCLUSIONS */}
        <motion.section ref={inclusionsRef} initial="hidden" whileInView="visible" viewport={{ once: false }} variants={fadeInUp} className="scroll-section">
          <div className="policy-box inclusion-box">
            <h4 className="p-title green"><FaCheckCircle /> What's Included</h4>
            <ul className="p-list">
              <li><FaHotel /> 6 Nights stay in 3-star category hotels (Bhubaneswar, Puri, Kolkata)</li>
              <li><FaUtensils /> Breakfast and Dinner at hotels (5 Breakfasts & 5 Dinners)</li>
              <li><FaStar /> Special Lunch – Shree Jagannath Mahaprasad</li>
              <li><FaBus /> AC transportation for all sightseeing and transfers</li>
              <li><FaCheckCircle /> Gangasagar ferry charges and all toll/parking</li>
              <li><FaShieldAlt /> Tour coordination and assistance</li>
            </ul>
          </div>
          <div className="policy-box exclusion-box">
            <h4 className="p-title red"><FaTimesCircle /> What's Excluded</h4>
            <ul className="p-list">
              <li><FaTimesCircle /> Airfare or train tickets for arrival and departure</li>
              <li><FaTimesCircle /> Entry fees for monuments and temples</li>
              <li><FaTimesCircle /> Personal expenses (Shopping, Laundry, Tips)</li>
              <li><FaShieldAlt /> Travel insurance or medical expenses</li>
              <li><FaTimesCircle /> Anything not mentioned in inclusions</li>
            </ul>
          </div>
        </motion.section>

        {/* ABOUT SECTION */}
        <motion.section ref={aboutRef} initial="hidden" whileInView="visible" viewport={{ once: false }} variants={fadeInUp} className="scroll-section">
          <h2 className="section-main-title">Tour Overview</h2>
          <p className="about-text">
            The Jagannath Puri Divine Darshan Yatra is a spiritually enriching journey covering the most sacred sites in Eastern India. From the ancient temples of Bhubaneswar to the architectural marvel of Konark and the spiritual confluence at Gangasagar, this tour offers a perfect blend of devotion and exploration.
          </p>
        </motion.section>

        {/* FOOTER LINKS */}
        <div className="footer-links-container" style={{marginTop: '40px'}}>
          <div className="accordion-item">
            <Link to="/terms-conditions" className="accordion-header" style={{ textDecoration: 'none', color: 'inherit' }}>
              <span>Terms & Conditions</span>
              <FaChevronDown className="arrow-icon" style={{ transform: 'rotate(-90deg)' }} /> 
            </Link>
          </div>

          <div className="accordion-item">
            <Link to="/privacy-policy" className="accordion-header" style={{ textDecoration: 'none', color: 'inherit' }}>
              <span>Privacy Policy</span>
              <FaChevronDown className="arrow-icon" style={{ transform: 'rotate(-90deg)' }} />
            </Link>
          </div>
        </div>

        {/* IMPORTANT NOTE */}
        <motion.section initial="hidden" whileInView="visible" viewport={{ once: false }} variants={fadeInUp} className="scroll-section travel-info-grid">
          <div className="info-card note-card" style={{width: '100%'}}>
            <h4 className="info-card-title"><FaPaperPlane /> Important Note</h4>
            <p>Flight and train tickets can be arranged by <strong>Chandrama Holidays</strong> on request at the best available fares to ensure a smooth journey.</p>
          </div>
        </motion.section>
      </div>
    </div>
  );
};

export default JagannathPuriYatra;